import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Popper,
  Paper,
  ClickAwayListener,
  MenuList,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WebIcon from "@mui/icons-material/Web";
import PaletteIcon from "@mui/icons-material/Palette";
import LanguageIcon from "@mui/icons-material/Language";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DescriptionIcon from "@mui/icons-material/Description";
import axios from "axios";
import { hostname } from "../utils";
import LoadingButton from "./LoadingButton";

interface WebArtifacts {
  id: string;
  name?: string;
  isDefault?: boolean;
}

interface CustomDomain {
  id: string;
  subdomain: string;
  status: string;
}

interface VariableField {
  key: string;
  label: string;
}

export interface LandingPageConfig {
  prompt: string;
  artifactsId?: string;  // Brand kit
  artifactsName?: string;
  domainId?: string;  // Custom domain
  domainName?: string;
  thumbnailUrl?: string;
}

interface LandingPageDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: LandingPageConfig, placeholder: string) => void;
  getToken: () => Promise<string>;
  companyId: string;
  existingConfig?: LandingPageConfig | null;
  customVariableFields?: VariableField[];
}

// Standard member fields for landing page personalization
const STANDARD_MEMBER_FIELDS = [
  { key: "firstName", label: "First Name", icon: <PersonIcon fontSize="small" /> },
  { key: "lastName", label: "Last Name", icon: <PersonIcon fontSize="small" /> },
  { key: "email", label: "Email", icon: <EmailIcon fontSize="small" /> },
  { key: "phone", label: "Phone", icon: <PhoneIcon fontSize="small" /> },
  { key: "company", label: "Company", icon: <BusinessIcon fontSize="small" /> },
  { key: "title", label: "Title", icon: <WorkIcon fontSize="small" /> },
  { key: "linkedinUrl", label: "LinkedIn URL", icon: <LinkedInIcon fontSize="small" /> },
];

// Special field for inserting all data as JSON
const FULL_RECORD_FIELD = {
  key: "fullRecord",
  label: "Full Record (All Data)",
  icon: <DescriptionIcon fontSize="small" />,
  isFullRecord: true,
};

const LandingPageDialog: React.FC<LandingPageDialogProps> = ({
  open,
  onClose,
  onSave,
  getToken,
  companyId,
  existingConfig,
  customVariableFields = [],
}) => {
  const [prompt, setPrompt] = useState("");
  const [linkText, setLinkText] = useState("View Your Personalized Page");
  const [selectedArtifactsId, setSelectedArtifactsId] = useState("");
  const [selectedDomainId, setSelectedDomainId] = useState("");
  const [artifacts, setArtifacts] = useState<WebArtifacts[]>([]);
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // For variable insertion menu (+ button)
  const [variableMenuAnchorEl, setVariableMenuAnchorEl] = useState<null | HTMLElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement | null>(null);

  // For slash command menu
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [slashMenuAnchorEl, setSlashMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [slashFilter, setSlashFilter] = useState("");
  const [slashStartPosition, setSlashStartPosition] = useState<number | null>(null);

  // Filter custom fields to exclude standard ones
  const uniqueCustomFields = customVariableFields.filter(
    (field) => !STANDARD_MEMBER_FIELDS.map((f) => f.key).includes(field.key)
  );

  // All available fields for slash command (including full record option)
  const allFields = [
    FULL_RECORD_FIELD,
    ...STANDARD_MEMBER_FIELDS,
    ...uniqueCustomFields.map((f) => ({ ...f, icon: <DataObjectIcon fontSize="small" /> })),
  ];

  // Filtered fields based on slash command input
  const filteredFields = slashFilter
    ? allFields.filter(
        (f) =>
          f.key.toLowerCase().includes(slashFilter.toLowerCase()) ||
          f.label.toLowerCase().includes(slashFilter.toLowerCase())
      )
    : allFields;

  // Load existing config when dialog opens
  useEffect(() => {
    if (open && existingConfig) {
      setPrompt(existingConfig.prompt || "");
      setSelectedArtifactsId(existingConfig.artifactsId || "");
      setSelectedDomainId(existingConfig.domainId || "");
    } else if (open && !existingConfig) {
      // Reset state when opening without existing config
      setPrompt("");
      setLinkText("View Your Personalized Page");
      setSelectedArtifactsId("");
      setSelectedDomainId("");
    }
  }, [open, existingConfig]);

  // Load artifacts (brand kits) and domains when dialog opens
  useEffect(() => {
    if (!open || !companyId) return;

    const loadData = async () => {
      setLoadingArtifacts(true);
      setLoadingDomains(true);
      setError("");

      try {
        const token = await getToken();

        // Load web artifacts (brand kits)
        const artifactsResponse = await axios.get(
          `${hostname}/web-artifacts`,
          {
            params: { companyId, listAll: true },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setArtifacts(artifactsResponse.data.items || []);

        // Auto-select default artifacts
        const defaultArtifacts = (artifactsResponse.data.items || []).find(
          (a: WebArtifacts) => a.isDefault
        );
        if (defaultArtifacts && !selectedArtifactsId) {
          setSelectedArtifactsId(defaultArtifacts.id);
        }
      } catch (err: any) {
        console.error("Error loading artifacts:", err);
      } finally {
        setLoadingArtifacts(false);
      }

      try {
        const token = await getToken();

        // Load custom domains
        const domainsResponse = await axios.get(
          `${hostname}/tracking-domains`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        // Filter to only verified domains
        const verifiedDomains = (domainsResponse.data.items || []).filter(
          (d: CustomDomain) => d.status === "verified"
        );
        setDomains(verifiedDomains);
      } catch (err: any) {
        console.error("Error loading domains:", err);
      } finally {
        setLoadingDomains(false);
      }
    };

    loadData();
  }, [open, companyId, getToken]);

  const handleSave = () => {
    if (!prompt.trim()) {
      setError("Please enter landing page instructions");
      return;
    }

    setSaving(true);

    const selectedArtifact = artifacts.find((a) => a.id === selectedArtifactsId);
    const selectedDomain = domains.find((d) => d.id === selectedDomainId);

    const config: LandingPageConfig = {
      prompt: prompt.trim(),
      artifactsId: selectedArtifactsId || undefined,
      artifactsName: selectedArtifact?.name || undefined,
      domainId: selectedDomainId || undefined,
      domainName: selectedDomain?.subdomain || undefined,
    };

    // Generate placeholder with link text
    const placeholder = linkText
      ? `{{LandingPage: "${linkText}"}}`
      : "{{LandingPage}}";

    onSave(config, placeholder);
    setSaving(false);
    onClose();
  };

  // Handle opening the + button menu
  const handleOpenVariableMenu = (event: React.MouseEvent<HTMLElement>) => {
    setVariableMenuAnchorEl(event.currentTarget);
  };

  // Handle inserting a variable from the menu
  const handleInsertVariable = (variableKey: string, isFullRecord?: boolean) => {
    const placeholder = isFullRecord ? "{{fullRecord}}" : `{{${variableKey}}}`;
    const input = promptInputRef.current;

    if (input) {
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const newPrompt = prompt.substring(0, start) + placeholder + prompt.substring(end);
      setPrompt(newPrompt);

      // Move cursor after the inserted placeholder
      setTimeout(() => {
        input.focus();
        const newPosition = start + placeholder.length;
        input.setSelectionRange(newPosition, newPosition);
      }, 0);
    } else {
      setPrompt(prompt + placeholder);
    }

    setVariableMenuAnchorEl(null);
  };

  // Handle inserting from slash command
  const handleSlashInsert = (variableKey: string, isFullRecord?: boolean) => {
    const placeholder = isFullRecord ? "{{fullRecord}}" : `{{${variableKey}}}`;
    const input = promptInputRef.current;

    if (input && slashStartPosition !== null) {
      // Remove the "/" and any filter text, then insert the placeholder
      const beforeSlash = prompt.substring(0, slashStartPosition);
      const afterFilter = prompt.substring(input.selectionStart || slashStartPosition);
      const newPrompt = beforeSlash + placeholder + afterFilter;
      setPrompt(newPrompt);

      // Move cursor after the inserted placeholder
      setTimeout(() => {
        input.focus();
        const newPosition = slashStartPosition + placeholder.length;
        input.setSelectionRange(newPosition, newPosition);
      }, 0);
    }

    setSlashMenuOpen(false);
    setSlashFilter("");
    setSlashStartPosition(null);
  };

  // Handle prompt text changes with slash command detection
  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart || 0;
    setPrompt(newValue);

    // Check if user just typed "/"
    const charBeforeCursor = newValue[cursorPosition - 1];
    const charBeforeSlash = cursorPosition > 1 ? newValue[cursorPosition - 2] : " ";

    if (charBeforeCursor === "/" && (charBeforeSlash === " " || charBeforeSlash === "\n" || cursorPosition === 1)) {
      // Open slash menu
      setSlashStartPosition(cursorPosition - 1);
      setSlashMenuAnchorEl(e.target);
      setSlashMenuOpen(true);
      setSlashFilter("");
    } else if (slashMenuOpen && slashStartPosition !== null) {
      // Update filter based on text after "/"
      const textAfterSlash = newValue.substring(slashStartPosition + 1, cursorPosition);

      // Close menu if user typed space or deleted the slash
      if (textAfterSlash.includes(" ") || cursorPosition <= slashStartPosition) {
        setSlashMenuOpen(false);
        setSlashFilter("");
        setSlashStartPosition(null);
      } else {
        setSlashFilter(textAfterSlash);
      }
    }
  };

  // Handle keyboard events for slash menu navigation
  const handlePromptKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (slashMenuOpen) {
      if (e.key === "Escape") {
        e.preventDefault();
        setSlashMenuOpen(false);
        setSlashFilter("");
        setSlashStartPosition(null);
      } else if (e.key === "Enter" && filteredFields.length > 0) {
        e.preventDefault();
        handleSlashInsert(filteredFields[0].key);
      } else if (e.key === "Tab" && filteredFields.length > 0) {
        e.preventDefault();
        handleSlashInsert(filteredFields[0].key);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WebIcon color="primary" />
            <Typography variant="h6">
              {existingConfig ? "Edit Landing Page" : "Add Landing Page"}
            </Typography>
            <Chip label="Beta" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Create a personalized landing page for each contact. The AI will generate a unique
          page based on your instructions and the contact's data when the email is sent.
        </Alert>

        {/* Landing Page Instructions with variable insertion */}
        <Box sx={{ position: "relative" }}>
          <TextField
            fullWidth
            label="Landing Page Instructions"
            placeholder="E.g., Create a personalized product demo page for {{firstName}} at {{company}}. Include our pricing table, a personalized greeting, and a calendar booking widget."
            multiline
            rows={5}
            value={prompt}
            onChange={handlePromptChange}
            onKeyDown={handlePromptKeyDown}
            inputRef={promptInputRef}
            sx={{ mb: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                  <IconButton
                    size="small"
                    onClick={handleOpenVariableMenu}
                    title="Insert personalization variable"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Type <strong>/</strong> to insert variables, or click the + button. Use {"{{firstName}}"}, {"{{company}}"}, {"{{email}}"}, etc.
          </Typography>

          {/* Slash command popup menu */}
          <Popper
            open={slashMenuOpen}
            anchorEl={slashMenuAnchorEl}
            placement="bottom-start"
            style={{ zIndex: 1400 }}
          >
            <ClickAwayListener onClickAway={() => { setSlashMenuOpen(false); setSlashFilter(""); setSlashStartPosition(null); }}>
              <Paper elevation={8} sx={{ maxHeight: 250, overflow: "auto", minWidth: 220 }}>
                <MenuList dense>
                  {filteredFields.length === 0 ? (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        No matching variables
                      </Typography>
                    </MenuItem>
                  ) : (
                    <>
                      <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                        Insert Variable
                      </Typography>
                      {filteredFields.map((field: any) => (
                        <MenuItem
                          key={field.key}
                          onClick={() => handleSlashInsert(field.key, field.isFullRecord)}
                          dense
                        >
                          <ListItemIcon>{field.icon}</ListItemIcon>
                          <ListItemText
                            primary={field.label}
                            secondary={field.isFullRecord ? "Insert all fields as JSON" : `{{${field.key}}}`}
                            secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
                          />
                        </MenuItem>
                      ))}
                    </>
                  )}
                </MenuList>
              </Paper>
            </ClickAwayListener>
          </Popper>
        </Box>

        {/* Variable insertion menu (+ button) */}
        <Menu
          anchorEl={variableMenuAnchorEl}
          open={Boolean(variableMenuAnchorEl)}
          onClose={() => setVariableMenuAnchorEl(null)}
          PaperProps={{ sx: { minWidth: 220, maxHeight: 400 } }}
        >
          {/* Full Record Option */}
          <MenuItem onClick={() => handleInsertVariable(FULL_RECORD_FIELD.key, true)} dense>
            <ListItemIcon>{FULL_RECORD_FIELD.icon}</ListItemIcon>
            <ListItemText
              primary={FULL_RECORD_FIELD.label}
              secondary="Insert all fields as JSON"
              secondaryTypographyProps={{ variant: "caption" }}
            />
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
            Member Fields
          </Typography>
          {STANDARD_MEMBER_FIELDS.map((field) => (
            <MenuItem key={field.key} onClick={() => handleInsertVariable(field.key)} dense>
              <ListItemIcon>{field.icon}</ListItemIcon>
              <ListItemText
                primary={field.label}
                secondary={`{{${field.key}}}`}
                secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
              />
            </MenuItem>
          ))}
          {uniqueCustomFields.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}>
                Custom Fields
              </Typography>
              {uniqueCustomFields.map((field) => (
                <MenuItem key={field.key} onClick={() => handleInsertVariable(field.key)} dense>
                  <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={field.label}
                    secondary={`{{${field.key}}}`}
                    secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
                  />
                </MenuItem>
              ))}
            </>
          )}
        </Menu>

        {/* Link Text */}
        <TextField
          fullWidth
          label="Link Text"
          placeholder="View Your Personalized Page"
          value={linkText}
          onChange={(e) => setLinkText(e.target.value)}
          sx={{ mb: 3 }}
          helperText="The clickable text that will appear in the email."
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <PaletteIcon fontSize="small" />
          Brand Kit (Optional)
        </Typography>

        {/* Brand Kit Selection */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Brand Kit</InputLabel>
          <Select
            value={selectedArtifactsId}
            onChange={(e) => setSelectedArtifactsId(e.target.value)}
            label="Brand Kit"
            disabled={loadingArtifacts}
          >
            <MenuItem value="">
              <em>None (AI will design freely)</em>
            </MenuItem>
            {loadingArtifacts ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading brand kits...
              </MenuItem>
            ) : artifacts.length === 0 ? (
              <MenuItem disabled>No brand kits available</MenuItem>
            ) : (
              artifacts.map((artifact) => (
                <MenuItem key={artifact.id} value={artifact.id}>
                  {artifact.name || "Default Brand Kit"}
                  {artifact.isDefault && (
                    <Chip label="Default" size="small" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />
                  )}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <LanguageIcon fontSize="small" />
          Custom Domain (Optional)
        </Typography>

        {/* Custom Domain Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Custom Domain</InputLabel>
          <Select
            value={selectedDomainId}
            onChange={(e) => setSelectedDomainId(e.target.value)}
            label="Custom Domain"
            disabled={loadingDomains}
          >
            <MenuItem value="">
              <em>Use default domain</em>
            </MenuItem>
            {loadingDomains ? (
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Loading domains...
              </MenuItem>
            ) : domains.length === 0 ? (
              <MenuItem disabled>No custom domains available</MenuItem>
            ) : (
              domains.map((domain) => (
                <MenuItem key={domain.id} value={domain.id}>
                  {domain.subdomain}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Typography variant="caption" color="text.secondary">
          The landing page will be hosted on the selected domain (e.g., https://yourdomain.com/lp/abc123).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          disabled={!prompt.trim()}
        >
          {existingConfig ? "Update Landing Page" : "Save Landing Page Config"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default LandingPageDialog;
